import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import "../../Styles/Seller/Analytics.css";
import {
  DEFAULT_PRODUCT_IMAGE,
  withDefaultProductImage,
} from "../../utils/productPresentation";

const data = [
  { name: "Jan", revenue: 2300 },
  { name: "Feb", revenue: 2800 },
  { name: "Mar", revenue: 3100 },
  { name: "Apr", revenue: 2800 },
  { name: "May", revenue: 3300 },
  { name: "Jun", revenue: 3800 },
  { name: "Jul", revenue: 4100 },
  { name: "Aug", revenue: 3700 },
  { name: "Sep", revenue: 4100 },
  { name: "Oct", revenue: 4500 },
  { name: "Nov", revenue: 4800 },
  { name: "Dec", revenue: 5200 },
];

const topProducts = [
  {
    rank: 1,
    image: "/images/product-thermometer.jpg", // replace with real path
    title: "Digital Thermometer Pro",
    subtitle: "Diagnostic Equipment",
    price: "$89.99",
    sold: 55,
  },
  {
    rank: 2,
    image: "/images/product-bp.jpg",
    title: "Blood Pressure Monitor Elite",
    subtitle: "Monitoring Devices",
    price: "$129.99",
    sold: 43,
  },
  {
    rank: 3,
    image: "/images/product-pulse.jpg",
    title: "Pulse Oximeter Advanced",
    subtitle: "Diagnostic Equipment",
    price: "$45.99",
    sold: 50,
  },
  {
    rank: 4,
    image: "/images/product-scale.jpg",
    title: "Digital Weight Scale Smart",
    subtitle: "Health Monitoring",
    price: "$69.99",
    sold: 41,
  },
];

const insights = [
  { label: "Repeat Customers", value: 68, color: "#22c55e" }, // green
  { label: "Customer Satisfaction", value: 92, color: "#2563eb" }, // blue
  { label: "Mobile Users", value: 45, color: "#f59e0b" }, // orange
  { label: "Email Open Rate", value: 34, color: "#8b5cf6" }, // purple
];

export default function Analytics({ TopsellingProduct }) {
  const exportReport = () => {
    window.print();
  };

  return (
    <>
      <div className="analytics-page">
        <div className="analytics-header">
          <h1>Analytics & Reports</h1>

          <div className="analytics-actions">
            <div className="date-picker">
              <input type="date" aria-label="Start date" />
              <span>→</span>
              <input type="date" aria-label="End date" />
            </div>

            <button className="export-btn" onClick={exportReport}>
              ⬇ Export Report
            </button>
          </div>
        </div>

        <div className="analytics-grid">
          <div className="analytics-chart-card">
            <h3>Revenue Trend</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={data}>
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
                    }}
                    itemStyle={{ color: "#0f172a" }}
                    formatter={(value) => new Intl.NumberFormat().format(value)}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#2b9cff"
                    strokeWidth={2.5}
                    dot={{ r: 4, stroke: "#fff", strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <aside className="analytics-metrics">
            <h4>Key Metrics</h4>

            <div className="metric">
              <p className="metric-label">Conversion Rate</p>
              <p className="metric-value">3.2%</p>
            </div>

            <div className="metric">
              <p className="metric-label">Average Order Value</p>
              <p className="metric-value">AED 89.5</p>
            </div>

            <div className="metric">
              <p className="metric-label">Customer Lifetime Value</p>
              <p className="metric-value">AED 456.78</p>
            </div>

            <div className="metric">
              <p className="metric-label">Return Rate</p>
              <p className="metric-value">2.1%</p>
            </div>
          </aside>
        </div>
      </div>
      <section className="pi-section">
        <div className="pi-grid">
          {/* Left: Top Selling Products */}
          <div className="pi-card pi-products">
            <div className="pi-card-header">
              <h3>Top Selling Products</h3>
            </div>

            <div className="pi-products-list">
              {TopsellingProduct &&
                TopsellingProduct.map((p, index) => (
                  <div key={index} className="pi-product-row">
                    <div className="pi-rank">#{index + 1}</div>

                    <div className="pi-thumb-and-info">
                      <img
                        src={withDefaultProductImage(
                          p.image_url || p.product_image || p.product_image_key
                        )}
                        alt={p.title}
                        className="pi-thumb"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                        }}
                      />

                      <div className="pi-info">
                        <div className="pi-title">{p.name}</div>
                        {/* <div className="pi-subtitle">{p.subtitle}</div> */}
                      </div>
                    </div>

                    <div className="pi-price-block">
                      <div className="pi-price">{p.price}</div>
                      <div className="pi-sold">{p.total_sold} sold</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Right: Customer Insights */}
          <aside className="pi-card pi-insights">
            <div className="pi-card-header">
              <h3>Customer Insights</h3>
            </div>

            <div className="pi-insights-list">
              {insights.map((ins, idx) => (
                <div className="pi-insight-row" key={ins.label}>
                  <div className="pi-insight-top">
                    <span className="pi-insight-label">{ins.label}</span>
                    <span className="pi-insight-value">{ins.value}%</span>
                  </div>

                  <div className="pi-progress-track">
                    <div
                      className="pi-progress-fill"
                      style={{ width: `${ins.value}%`, background: ins.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
